import type { DrawnLine } from "../types/chart-types";
import { calculatePaddedPriceRange, formatPriceValue } from "./chart-utils";

interface DrawLinesParams {
  ctx: CanvasRenderingContext2D;
  drawnLines: DrawnLine[];
  visibleData: {
    time: number;
    timestamp: number;
    price?: number;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
  }[];
  allData: { time: number; timestamp: number }[];
  chartType: "line" | "candlestick" | "heikin-ashi" | "bars";
  chartHeight: number;
  baseChartWidth: number;
  gridEndX: number;
  xDomainStart: number;
  windowSize: number;
  yScaleFactor?: number;
  pricePrecision?: number;
  smoothedMinPrice?: number;
  smoothedPriceRange?: number;
}

/** Convert a real timestamp (ms) to canvas X using binary-search interpolation.
 *  Finds the two nearest data points by timestamp and interpolates/extrapolates locally.
 *  This avoids the drift caused by global linear interpolation when the time↔timestamp
 *  slope is non-uniform across the dataset (e.g. line chart history vs live points). */
function tsToX(
  ts: number,
  data: { time: number; timestamp: number }[],
  xDomainStart: number,
  windowSize: number,
  baseChartWidth: number,
): number {
  if (data.length < 2) return -1;

  const toX = (timeIndex: number) => ((timeIndex - xDomainStart) / windowSize) * baseChartWidth;

  // Extrapolate before data range using the first two points
  if (ts <= data[0]!.timestamp) {
    const p0 = data[0]!,
      p1 = data[1]!;
    if (p1.timestamp === p0.timestamp) return toX(p0.time);
    const ratio = (ts - p0.timestamp) / (p1.timestamp - p0.timestamp);
    return toX(p0.time + ratio * (p1.time - p0.time));
  }

  // Extrapolate beyond data range using the last two points
  const last = data[data.length - 1]!;
  if (ts >= last.timestamp) {
    const p0 = data[data.length - 2]!,
      p1 = last;
    if (p1.timestamp === p0.timestamp) return toX(p1.time);
    const ratio = (ts - p0.timestamp) / (p1.timestamp - p0.timestamp);
    return toX(p0.time + ratio * (p1.time - p0.time));
  }

  // Binary search for the two surrounding points by timestamp
  let lo = 0,
    hi = data.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (data[mid]!.timestamp <= ts) lo = mid;
    else hi = mid;
  }
  const p0 = data[lo]!,
    p1 = data[hi]!;
  if (p1.timestamp === p0.timestamp) return toX(p0.time);
  const ratio = (ts - p0.timestamp) / (p1.timestamp - p0.timestamp);
  return toX(p0.time + ratio * (p1.time - p0.time));
}

export { tsToX };

/** Extend a ray from (x1,y1) through (x2,y2) forward only, to the chart boundary. */
function rayToBounds(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  gridEndX: number,
  chartHeight: number,
): { ex: number; ey: number } | null {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return null;

  const ts: number[] = [];
  if (dx > 0) ts.push((gridEndX - x1) / dx);
  else if (dx < 0) ts.push((0 - x1) / dx);
  if (dy > 0) ts.push((chartHeight - y1) / dy);
  else if (dy < 0) ts.push((0 - y1) / dy);

  const tEnd = ts.filter((t) => t > 0).reduce((a, b) => Math.min(a, b), Infinity);
  if (!isFinite(tEnd)) return null;

  return { ex: x1 + tEnd * dx, ey: y1 + tEnd * dy };
}

/** Minimum pixel span between p1 and p2 so the line stays visible and interactive at any zoom level. */
const MIN_VISUAL_SPAN = 40;

/** Minimum pixel size for rectangle width/height so it stays visible and interactive at any zoom level. */
const MIN_RECT_SIZE = 30;

/**
 * Ensures the rectangle has at least MIN_RECT_SIZE px in each dimension.
 * p1 stays fixed; p2 is expanded if needed.
 */
export function adjustRectHandles(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { rx1: number; ry1: number; rx2: number; ry2: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const rx2 = Math.abs(dx) < MIN_RECT_SIZE ? x1 + (dx >= 0 ? MIN_RECT_SIZE : -MIN_RECT_SIZE) : x2;
  const ry2 = Math.abs(dy) < MIN_RECT_SIZE ? y1 + (dy >= 0 ? MIN_RECT_SIZE : -MIN_RECT_SIZE) : y2;
  return { rx1: x1, ry1: y1, rx2, ry2 };
}

/**
 * Returns visual positions of p1/p2, expanded symmetrically around their midpoint
 * to guarantee at least MIN_VISUAL_SPAN pixels between them.
 * The actual stored ts/price values are unchanged — rendering only.
 */
export function adjustHandleSpan(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { vx1: number; vy1: number; vx2: number; vy2: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist >= MIN_VISUAL_SPAN) return { vx1: x1, vy1: y1, vx2: x2, vy2: y2 };
  // p1 stays fixed at stored position; only p2 is pushed to MIN_VISUAL_SPAN distance.
  // Same approach as adjustRectHandles — avoids both handles drifting off the line.
  const nx = dist < 0.5 ? 1 : dx / dist;
  const ny = dist < 0.5 ? 0 : dy / dist;
  return { vx1: x1, vy1: y1, vx2: x1 + nx * MIN_VISUAL_SPAN, vy2: y1 + ny * MIN_VISUAL_SPAN };
}

export function drawLines({
  ctx,
  drawnLines,
  visibleData,
  allData,
  chartType,
  chartHeight,
  baseChartWidth,
  gridEndX,
  xDomainStart,
  windowSize,
  yScaleFactor,
  pricePrecision,
  smoothedMinPrice,
  smoothedPriceRange,
}: DrawLinesParams): void {
  if (drawnLines.length === 0 || visibleData.length === 0) return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, gridEndX, chartHeight);
  ctx.clip();

  let _rawMin: number;
  let _rawRange: number;
  if (smoothedMinPrice != null && smoothedPriceRange != null) {
    _rawMin = smoothedMinPrice;
    _rawRange = smoothedPriceRange;
  } else {
    ({ paddedMinPrice: _rawMin, paddedPriceRange: _rawRange } = calculatePaddedPriceRange({
      visibleData,
      chartType,
    }));
  }
  const _sf = yScaleFactor ?? 1;
  const paddedMinPrice = _rawMin + _rawRange / 2 - _rawRange / (2 * _sf);
  const paddedPriceRange = _rawRange / _sf;

  // Use allData for tsToX so lines remain visible even when scrolled/zoomed far from their position
  const refData = allData.length >= 2 ? allData : visibleData;
  const getY = (price: number) => chartHeight - ((price - paddedMinPrice) / paddedPriceRange) * chartHeight;
  const getX = (ts: number) => tsToX(ts, refData, xDomainStart, windowSize, baseChartWidth);

  drawnLines.forEach((line) => {
    ctx.save();
    ctx.strokeStyle = line.color || "#F5A524";
    ctx.lineWidth = line.strokeWidth || 1;
    ctx.globalAlpha = 0.85;

    if (line.type === "horizontal") {
      const y = getY(line.price);
      if (y >= 0 && y <= chartHeight) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(gridEndX, y);
        ctx.stroke();

        const dotX = gridEndX / 2;
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.arc(dotX, y, 6, 0, Math.PI * 2);
        ctx.fill();

        // Price text above the line at the right edge, same color as line
        const priceText = formatPriceValue(line.price, pricePrecision);
        ctx.font = "11px sans-serif";
        ctx.textBaseline = "bottom";
        ctx.textAlign = "right";
        ctx.globalAlpha = 1;
        ctx.fillStyle = line.color || "#F5A524";
        ctx.fillText(priceText, gridEndX - 4, y - 2);
      }
    } else if (line.type === "vertical") {
      const x = getX(line.ts);
      if (x >= 0 && x <= gridEndX) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, chartHeight);
        ctx.stroke();

        const dotY = chartHeight / 2;
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.arc(x, dotY, 6, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (line.type === "trend-line") {
      const x1 = getX(line.ts1);
      const y1 = getY(line.price1);
      const x2 = getX(line.ts2);
      const y2 = getY(line.price2);

      // Use visually adjusted positions for both line and handles — ensures the line
      // stays visible when zoomed out to HTF where p1/p2 may collapse to the same pixel.
      const { vx1, vy1, vx2, vy2 } = adjustHandleSpan(x1, y1, x2, y2);
      ctx.beginPath();
      ctx.moveTo(vx1, vy1);
      ctx.lineTo(vx2, vy2);
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(vx1, vy1, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(vx2, vy2, 6, 0, Math.PI * 2);
      ctx.fill();
    } else if (line.type === "ray") {
      const x1 = getX(line.ts1);
      const y1 = getY(line.price1);
      const x2 = getX(line.ts2);
      const y2 = getY(line.price2);

      // Use visually adjusted positions so direction stays well-defined even on HTF
      const { vx1, vy1, vx2, vy2 } = adjustHandleSpan(x1, y1, x2, y2);
      const dx = vx2 - vx1;
      const dy = vy2 - vy1;

      if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001) {
        const ts: number[] = [];
        if (dx > 0) ts.push((gridEndX - vx1) / dx);
        else if (dx < 0) ts.push((0 - vx1) / dx);
        if (dy > 0) ts.push((chartHeight - vy1) / dy);
        else if (dy < 0) ts.push((0 - vy1) / dy);

        const tEnd = ts.filter((t) => t > 0).reduce((a, b) => Math.min(a, b), Infinity);
        const endX = vx1 + tEnd * dx;
        const endY = vy1 + tEnd * dy;

        ctx.beginPath();
        ctx.moveTo(vx1, vy1);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(vx1, vy1, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(vx2, vy2, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (line.type === "fibonacci-retracement") {
      const FIBO_LEVELS = [
        { ratio: 0, label: "0%" },
        { ratio: 0.236, label: "23.6%" },
        { ratio: 0.382, label: "38.2%" },
        { ratio: 0.5, label: "50%" },
        { ratio: 0.618, label: "61.8%" },
        { ratio: 0.786, label: "78.6%" },
        { ratio: 1, label: "100%" },
      ];
      const x1 = getX(line.ts1);
      const y1 = getY(line.price1);
      const x2 = getX(line.ts2);
      const y2 = getY(line.price2);

      ctx.font = "10px sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";

      const xLeft = Math.min(x1, x2);
      const xRight = Math.max(x1, x2);
      const color = line.color || "#F5A524";

      // Background bands between levels
      const yLevels = FIBO_LEVELS.map(({ ratio }) => {
        const price = line.price1 + ratio * (line.price2 - line.price1);
        return getY(price);
      });
      ctx.fillStyle = color;
      for (let i = 0; i < yLevels.length - 1; i++) {
        const yTop = Math.min(yLevels[i]!, yLevels[i + 1]!);
        const yBot = Math.max(yLevels[i]!, yLevels[i + 1]!);
        ctx.globalAlpha = 0.05;
        ctx.fillRect(xLeft, yTop, xRight - xLeft, yBot - yTop);
      }

      // Lines and labels
      for (let i = 0; i < FIBO_LEVELS.length; i++) {
        const { ratio, label } = FIBO_LEVELS[i]!;
        const y = yLevels[i]!;

        ctx.globalAlpha = ratio === 0 || ratio === 1 ? 0.85 : 0.6;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(xLeft, y!);
        ctx.lineTo(xRight, y!);
        ctx.stroke();

        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        ctx.fillText(label, xRight - 4, y! - 2);
      }

      // Dashed connector between p1 and p2
      ctx.setLineDash([4, 4]);
      ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Handles
      const { vx1: fx1, vy1: fy1, vx2: fx2, vy2: fy2 } = adjustHandleSpan(x1, y1, x2, y2);
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(fx1, fy1, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(fx2, fy2, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (line.type === "fibonacci-fan") {
      const FAN_LEVELS = [
        { ratio: 0, label: "0" },
        { ratio: 0.25, label: "0.25" },
        { ratio: 0.382, label: "0.382" },
        { ratio: 0.5, label: "0.5" },
        { ratio: 0.618, label: "0.618" },
        { ratio: 0.75, label: "0.75" },
        { ratio: 1, label: "1" },
        { ratio: 1.618, label: "1.618" },
        { ratio: 2, label: "2" },
      ];

      const x1 = getX(line.ts1);
      const y1 = getY(line.price1);
      const x2 = getX(line.ts2);
      const y2 = getY(line.price2);
      const color = line.color || "#00C076";

      // p1 = origin, p2 = ref — fan follows user's drag direction
      const originX = x1;
      const originY = y1;
      const refX = x2;
      const refY = y2;

      const xMin = Math.min(x1, x2);
      const xMax = Math.max(x1, x2);
      const yMin = Math.min(y1, y2);
      const yMax = Math.max(y1, y2);

      ctx.font = "10px sans-serif";

      const drawRay = (targetX: number, targetY: number, label: string, alpha: number, showLabel: boolean) => {
        if (Math.abs(targetX - originX) < 0.001 && Math.abs(targetY - originY) < 0.001) return;
        const end = rayToBounds(originX, originY, targetX, targetY, gridEndX, chartHeight);
        if (!end) return;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(originX, originY);
        ctx.lineTo(end.ex, end.ey);
        ctx.stroke();
        if (!showLabel) return;
        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        const atRight = end.ex >= gridEndX - 2;
        const atLeft = end.ex <= 2;
        if (atRight) {
          ctx.textAlign = "right";
          ctx.textBaseline = "middle";
          ctx.fillText(label, end.ex - 4, end.ey);
        } else if (atLeft) {
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(label, end.ex + 4, end.ey);
        } else {
          ctx.textAlign = "center";
          ctx.textBaseline = end.ey <= 2 ? "top" : "bottom";
          ctx.fillText(label, Math.max(20, Math.min(end.ex, gridEndX - 20)), end.ey + (end.ey <= 2 ? 4 : -4));
        }
      };

      // Set 1: rays to right edge (refX) at different Y — "horizontal" fan (low angles)
      for (const { ratio, label } of FAN_LEVELS) {
        const alpha = ratio === 0 || ratio === 1 ? 0.85 : 0.6;
        drawRay(refX, originY + ratio * (refY - originY), label, alpha, false);
      }

      // Set 2: rays to top edge (refY) at different X — pass through top of each vertical line
      // Set 2: rays to top edge at different X — skip ratio=1 only (duplicates Set 1 diagonal)
      for (const { ratio } of FAN_LEVELS) {
        if (ratio === 1) continue;
        const alpha = ratio === 0 ? 0.85 : 0.6;
        drawRay(originX + ratio * (refX - originX), refY, "", alpha, false);
      }

      // Horizontal grid lines — ratio 0..1 only, from xMin to xMax
      for (const { ratio, label } of FAN_LEVELS) {
        if (ratio < 0 || ratio > 1) continue;
        const yLevel = originY + ratio * (refY - originY);
        ctx.globalAlpha = ratio === 0 || ratio === 1 ? 0.85 : 0.5;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(xMin, yLevel);
        ctx.lineTo(xMax, yLevel);
        ctx.stroke();

        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        // Left side
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.fillText(label, xMin - 4, yLevel);
        // Right side
        ctx.textAlign = "left";
        ctx.fillText(label, xMax + 4, yLevel);
      }

      // Vertical grid lines — ratio 0..1 only, from yMin to yMax
      for (const { ratio, label } of FAN_LEVELS) {
        if (ratio < 0 || ratio > 1) continue;
        const xLevel = originX + ratio * (refX - originX);
        ctx.globalAlpha = ratio === 0 || ratio === 1 ? 0.85 : 0.5;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(xLevel, yMin);
        ctx.lineTo(xLevel, yMax);
        ctx.stroke();

        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        ctx.textAlign = "center";
        // Top side
        ctx.textBaseline = "bottom";
        ctx.fillText(label, xLevel, yMin - 4);
        // Bottom side
        ctx.textBaseline = "top";
        ctx.fillText(label, xLevel, yMax + 4);
      }

      // Handles at actual p1 and p2
      const { vx1: ff1, vy1: fg1, vx2: ff2, vy2: fg2 } = adjustHandleSpan(x1, y1, x2, y2);
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(ff1, fg1, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(ff2, fg2, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (line.type === "parallel-channel") {
      // p1 = bottom-left (A), p2 = bottom-right (B), p3 = top-right (C)
      // top-left (D) = A + (C - B)  →  drag p1 keeps right edge (B→C) fixed
      const x1 = getX(line.ts1); // A
      const y1 = getY(line.price1);
      const x2 = getX(line.ts2); // B
      const y2 = getY(line.price2);
      const x3 = getX(line.ts3); // C (top-right, stored explicitly)
      const y3 = getY(line.price3);
      const x4 = x1 + (x3 - x2); // D (top-left, computed)
      const y4 = y1 + (y3 - y2);

      const drawSegment = (ax: number, ay: number, bx: number, by: number, alpha: number, dashed = false) => {
        if (dashed) ctx.setLineDash([5, 5]);
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
        if (dashed) ctx.setLineDash([]);
      };

      // Fill
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = line.color || "#F5A524";
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.lineTo(x4, y4);
      ctx.closePath();
      ctx.fill();

      // Bottom line A→B, top line D→C, dashed middle
      drawSegment(x1, y1, x2, y2, 0.85);
      drawSegment(x4, y4, x3, y3, 0.85);
      drawSegment((x1 + x4) / 2, (y1 + y4) / 2, (x2 + x3) / 2, (y2 + y3) / 2, 0.5, true);

      // Handles: p1 and p2 at their corners; p3 at midpoint of top line (D→C)
      const topMidX = (x4 + x3) / 2;
      const topMidY = (y4 + y3) / 2;
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(x1, y1, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x2, y2, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(topMidX, topMidY, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (line.type === "pitchfork") {
      const xA = getX(line.ts1);
      const yA = getY(line.price1);
      const xB = getX(line.ts2);
      const yB = getY(line.price2);
      const xC = getX(line.ts3);
      const yC = getY(line.price3);

      const xM = (xB + xC) / 2;
      const yM = (yB + yC) / 2;
      const mdx = xM - xA;
      const mdy = yM - yA;

      const color = line.color || "#00C076";
      const innerColor = "#FF4D4D";

      // All 5 rays start from the B-C bar, direction = median (xM-xA, yM-yA)
      // Median starts from M (midpoint B-C), not from A — fills don't extend past handles
      const xUH = (xB + xM) / 2,
        yUH = (yB + yM) / 2;
      const xLH = (xC + xM) / 2,
        yLH = (yC + yM) / 2;

      const medEnd = rayToBounds(xM, yM, xM + mdx, yM + mdy, gridEndX, chartHeight);
      const upEnd = rayToBounds(xB, yB, xB + mdx, yB + mdy, gridEndX, chartHeight);
      const loEnd = rayToBounds(xC, yC, xC + mdx, yC + mdy, gridEndX, chartHeight);
      const upHEnd = rayToBounds(xUH, yUH, xUH + mdx, yUH + mdy, gridEndX, chartHeight);
      const loHEnd = rayToBounds(xLH, yLH, xLH + mdx, yLH + mdy, gridEndX, chartHeight);

      // Fill zones between adjacent parallel lines
      const fillBetween = (
        sx1: number,
        sy1: number,
        e1: { ex: number; ey: number } | null,
        sx2: number,
        sy2: number,
        e2: { ex: number; ey: number } | null,
        fillColor: string,
      ) => {
        if (!e1 || !e2) return;
        ctx.globalAlpha = 0.1;
        ctx.fillStyle = fillColor;
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(e1.ex, e1.ey);
        ctx.lineTo(e2.ex, e2.ey);
        ctx.lineTo(sx2, sy2);
        ctx.closePath();
        ctx.fill();
      };

      fillBetween(xB, yB, upEnd, xUH, yUH, upHEnd, color);
      fillBetween(xUH, yUH, upHEnd, xM, yM, medEnd, innerColor);
      fillBetween(xM, yM, medEnd, xLH, yLH, loHEnd, innerColor);
      fillBetween(xLH, yLH, loHEnd, xC, yC, loEnd, color);

      // B↔C connector bar
      ctx.globalAlpha = 0.7;
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(xB, yB);
      ctx.lineTo(xC, yC);
      ctx.stroke();

      // Solid handle line from A to M (single controller → one solid line)
      ctx.globalAlpha = 0.9;
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(xA, yA);
      ctx.lineTo(xM, yM);
      ctx.stroke();

      // Draw 5 parallel rays
      const drawRay = (sx: number, sy: number, end: { ex: number; ey: number } | null, c: string) => {
        if (!end) return;
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = c;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(end.ex, end.ey);
        ctx.stroke();
      };

      drawRay(xB, yB, upEnd, color);
      drawRay(xUH, yUH, upHEnd, innerColor);
      drawRay(xM, yM, medEnd, color);
      drawRay(xLH, yLH, loHEnd, innerColor);
      drawRay(xC, yC, loEnd, color);

      // Handles at A, B, C
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(xA, yA, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(xB, yB, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(xC, yC, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (line.type === "rectangle") {
      const x1 = getX(line.ts1);
      const y1 = getY(line.price1);
      const x2 = getX(line.ts2);
      const y2 = getY(line.price2);

      // Always draw at real coordinates so shape covers the same candles at any zoom level
      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const w = Math.abs(x2 - x1);
      const h = Math.abs(y2 - y1);

      // Fill
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = line.color || "#F5A524";
      ctx.fillRect(left, top, w, h);

      // Border
      ctx.globalAlpha = 0.85;
      ctx.strokeRect(left, top, w, h);

      // Handles at adjusted corners so visual position matches the hit zone in useChartInteraction.
      // Clamp to chart bounds so handles near the right/top/bottom edge stay clickable.
      const { rx1: hx1, ry1: hy1, rx2: hx2r, ry2: hy2r } = adjustRectHandles(x1, y1, x2, y2);
      const hx2 = Math.max(0, Math.min(gridEndX, hx2r));
      const hy2 = Math.max(0, Math.min(chartHeight, hy2r));
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(hx1, hy1, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(hx2, hy2, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  });

  ctx.restore();
}
